import React, { useState } from 'react';
import PropTypes from 'prop-types';
import rrService from '../services/rrService';

/**
 * RestrictedBuysNotice - Shown when an invest-cash rebalance produced no
 * trades because every underweight position is blocked by a buy restriction.
 *
 * Displays the list of restricted securities and offers a
 * "Buy to Max Targets" button that re-runs the rebalancer in REAL TIME,
 * allowing buys up to each sleeve's / model's MAXIMUM target weight
 * (never over the max), so excess cash can still be put to work in
 * unrestricted models. The result is returned synchronously and passed
 * to onRerunComplete.
 */
const RestrictedBuysNotice = ({ restrictions = [], groupName, onRerunComplete = null }) => {
    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState(null);

    if (!restrictions || restrictions.length === 0) return null;

    const runBuyToMax = async () => {
        setIsRunning(true);
        setError(null);
        try {
            const result = await rrService.runBuyToMaxForGroup(groupName);
            if (onRerunComplete) onRerunComplete(result);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Buy to max rebalance failed');
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className="alert alert-warning d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
            <div>
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>We can not buy anything that is underweight because of restriction(s):</strong>
                <ul className="mb-0 mt-1">
                    {restrictions.map((r, i) => (
                        <li key={i}>{r}</li>
                    ))}
                </ul>
                {error && (
                    <div className="text-danger mt-1">
                        <i className="bi bi-x-circle me-1"></i>
                        {error}
                    </div>
                )}
            </div>
            <div className="d-flex align-items-center">
                <button
                    className="btn btn-warning btn-sm"
                    onClick={runBuyToMax}
                    disabled={isRunning}
                    title="Rerun the rebalancer allowing buys up to each sleeve's and model's MAXIMUM target weight (never over the max)"
                >
                    {isRunning ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Rebalancing…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-bullseye me-1"></i>
                            Buy to Max Targets
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

RestrictedBuysNotice.propTypes = {
    restrictions: PropTypes.arrayOf(PropTypes.string),
    groupName: PropTypes.string.isRequired,
    onRerunComplete: PropTypes.func
};

export default RestrictedBuysNotice;
